import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Sparkles, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';

const AUTHORS = [
  {
    name: "Mark Twain",
    image: "/mark-twain-bg.jpg",
    quote: "The secret of getting ahead is getting started.",
    tip: "Write what you know. Your unique experiences are your greatest asset.",
    era: "1835-1910",
    works: "The Adventures of Tom Sawyer, Adventures of Huckleberry Finn"
  },
  {
    name: "Ernest Hemingway",
    image: "/hemingway-bg.jpg",
    quote: "There is nothing to writing. All you do is sit down and bleed.",
    tip: "Write drunk, edit sober. Get your ideas down first, polish later.",
    era: "1899-1961",
    works: "The Old Man and the Sea, A Farewell to Arms"
  },
  {
    name: "Oscar Wilde",
    image: "/oscar-wilde-bg.jpg",
    quote: "Be yourself; everyone else is already taken.",
    tip: "Develop your unique voice. Don't imitate—innovate.",
    era: "1854-1900",
    works: "The Picture of Dorian Gray, The Importance of Being Earnest"
  },
  {
    name: "Virginia Woolf",
    image: "/virginia-woolf-bg.jpg",
    quote: "You cannot find peace by avoiding life.",
    tip: "Explore the inner world of your characters. Stream of consciousness reveals truth.",
    era: "1882-1941",
    works: "Mrs Dalloway, To the Lighthouse"
  },
  {
    name: "Charles Dickens",
    image: "/charles-dickens-bg.jpg",
    quote: "There is nothing in the world so irresistibly contagious as laughter and good humor.",
    tip: "Create memorable characters with distinctive quirks and speech patterns.",
    era: "1812-1870",
    works: "A Tale of Two Cities, Great Expectations"
  },
  {
    name: "Edgar Allan Poe",
    image: "/edgar-poe-bg.jpg",
    quote: "I became insane, with long intervals of horrible sanity.",
    tip: "Build atmosphere and tension. Every word should serve the mood.",
    era: "1809-1849",
    works: "The Raven, The Tell-Tale Heart"
  },
  {
    name: "Jane Austen",
    image: "/jane-austen-bg.jpg",
    quote: "It is a truth universally acknowledged, that a single man in possession of a good fortune, must be in want of a wife.",
    tip: "Master the art of social observation. Wit and irony are powerful tools.",
    era: "1775-1817",
    works: "Pride and Prejudice, Sense and Sensibility"
  }
];

// Get daily author based on day of year
const getDailyAuthor = () => {
  const today = new Date();
  const dayOfYear = Math.floor(
    (today - new Date(today.getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24)
  );
  return AUTHORS[dayOfYear % AUTHORS.length];
};

// Get random author
const getRandomAuthor = () => {
  return AUTHORS[Math.floor(Math.random() * AUTHORS.length)];
};

export function TodaysInspiration({ showRefresh = true, compact = false }) {
  const [author, setAuthor] = useState(getDailyAuthor());
  const [imageError, setImageError] = useState(false);

  const handleRefresh = () => {
    let newAuthor = getRandomAuthor();
    while (newAuthor.name === author.name && AUTHORS.length > 1) {
      newAuthor = getRandomAuthor();
    }
    setAuthor(newAuthor);
    setImageError(false);
  };

  if (compact) {
    return (
      <div className="bg-gradient-to-r from-purple-600/10 to-blue-600/10 rounded-lg p-4 border border-purple-200">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
            {!imageError ? (
              <img 
                src={author.image} 
                alt={author.name}
                className="w-full h-full object-cover"
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-purple-100 text-purple-600 font-bold text-lg">
                {author.name.charAt(0)}
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm italic text-gray-700 truncate">"{author.quote}"</p>
            <p className="text-xs text-purple-600 font-medium">— {author.name}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Card className="overflow-hidden bg-gradient-to-br from-purple-50 to-blue-50 border-purple-200">
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          {/* Compact Author Image */}
          <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-gray-200">
            {!imageError ? (
              <img 
                src={author.image} 
                alt={author.name}
                className="w-full h-full object-cover"
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-400 to-blue-400 text-white text-2xl font-bold">
                {author.name.charAt(0)}
              </div>
            )}
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2 text-purple-600">
                <Sparkles className="w-4 h-4" />
                <span className="font-semibold text-sm">Today's Inspiration</span>
              </div>
              {showRefresh && (
                <Button variant="ghost" size="sm" onClick={handleRefresh} className="h-6 w-6 p-0">
                  <RefreshCw className="w-3 h-3" />
                </Button>
              )}
            </div>
            
            <blockquote className="text-base italic text-gray-700 mb-2 line-clamp-2">
              "{author.quote}"
            </blockquote>
            
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-800">{author.name}</p>
                <p className="text-xs text-gray-500">{author.era}</p>
              </div>
              <div className="text-xs text-purple-600 bg-purple-100 px-2 py-1 rounded">
                💡 {author.tip.substring(0, 40)}...
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default TodaysInspiration;
